/**
 * Categorised + searchable merge-tag picker.
 *
 * Used in two places:
 *   - inside the rich-text editor toolbar (insert as a TipTap
 *     chip via the `mergeTag` extension)
 *   - next to plain-text inspector fields (insert literal
 *     `{{ x.y }}` at the caret position)
 *
 * Keeps the same UI in both so operators don't have to learn two
 * different patterns. The host decides what happens on `onPick`
 * (chip command vs. string splicing).
 */

"use client"

import { useMemo, useState } from "react"
import { Search } from "lucide-react"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover"
import type { MergeTag, MergeTagRegistry } from "../blocks/email-builder/merge-tags"

export interface MergeTagPickerProps {
  registry: MergeTagRegistry
  /** Called with the picked tag. Closes the popover. */
  onPick: (tag: MergeTag) => void
  /**
   * Trigger element — must be a single React element so the
   * popover can clone / wrap it (refs + handlers attach via
   * base-ui's `render` prop).
   */
  children: React.ReactElement
  /** Side of the trigger to align the popover. */
  align?: "start" | "center" | "end"
}

export function MergeTagPicker({
  registry,
  onPick,
  children,
  align = "start",
}: MergeTagPickerProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return registry
    return registry
      .map((cat) => ({
        ...cat,
        tags: cat.tags.filter(
          (t) =>
            t.name.toLowerCase().includes(q) ||
            t.value.toLowerCase().includes(q) ||
            cat.name.toLowerCase().includes(q),
        ),
      }))
      .filter((cat) => cat.tags.length > 0)
  }, [registry, query])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger render={children} />
      <PopoverContent
        className="w-72 p-0"
        align={align}
        sideOffset={6}
      >
        <div className="border-b border-black/5 px-2 py-1.5 dark:border-white/5">
          <div className="flex items-center gap-1.5 rounded-md bg-black/5 px-2 py-1 dark:bg-white/5">
            <Search className="size-3.5 shrink-0 text-[var(--muted-foreground)]" />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search merge tags…"
              className="w-full bg-transparent text-xs outline-none placeholder:text-[var(--muted-foreground)]/60"
            />
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <div className="px-2 py-3 text-center text-xs text-[var(--muted-foreground)]">
              No matching tags
            </div>
          ) : (
            filtered.map((category) => (
              <div key={category.id} className="py-1">
                <div className="px-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--muted-foreground)]/70">
                  {category.name}
                </div>
                <ul>
                  {category.tags.map((tag) => (
                    <li key={tag.id}>
                      <button
                        type="button"
                        onClick={() => {
                          onPick(tag)
                          setOpen(false)
                          setQuery("")
                        }}
                        className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-xs text-[var(--foreground)] hover:bg-[var(--foreground)]/5"
                      >
                        <span className="min-w-0 truncate font-medium">
                          {tag.name}
                        </span>
                        <span className="shrink-0 font-mono text-[10px] text-[var(--muted-foreground)]/70">
                          {tag.value}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
